import { compressImage, fileExt, LONG_CACHE, PHOTO_MAX_SIDE } from "@/lib/image-compress";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Send, ImagePlus, X, MoreHorizontal, ThumbsUp, CheckCircle2, Pin, MessageCircle,
  Plus, Coffee, Lock, Stethoscope, EyeOff, Eye,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CitySelect } from "@/components/CitySelect";
import { DistrictSelect } from "@/components/DistrictSelect";
import { InviteToCoffeeDialog } from "@/components/InviteToCoffeeDialog";
import { ShareRequestButton } from "@/components/ShareRequestButton";
import { RequestsModeration } from "@/components/requests/RequestsModeration";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatPlace } from "@/lib/districts";
import { THREAD_TOPICS, expertCategoryFor, sortTopLevel, topicOf, parseMentions, encodeMentions, mentionQueryAt, MAX_MENTIONS } from "@/lib/request-threads";

type RequestsSearch = { request?: string; responses?: boolean };

export const Route = createFileRoute("/_authenticated/requests")({
  head: () => ({
    meta: [
      { title: "Спроси своих — TurkeyDrive" },
      { name: "description", content: "Задай вопрос сообществу и получи ответы от своих в Турции: жильё, документы, дети, здоровье, быт." },
      { property: "og:title", content: "Спроси своих — TurkeyDrive" },
      { property: "og:description", content: "Задай вопрос сообществу и получи ответы от своих в Турции." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): RequestsSearch => {
    const out: RequestsSearch = {};
    if (typeof s.request === "string" && /^[0-9a-f-]{36}$/i.test(s.request)) out.request = s.request;
    if (s.responses === true || s.responses === "true" || s.responses === "1" || s.responses === 1) out.responses = true;
    return out;
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData({ queryKey: ["requests", "published"], queryFn: fetchPublishedRequests });
  },
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-white px-4 py-12 text-center">
      <h1 className="text-xl font-semibold text-td-dark">Не удалось загрузить вопросы</h1>
      <p className="mt-2 text-sm text-td-muted">{(error as Error).message}</p>
    </div>
  ),
  notFoundComponent: () => <div className="min-h-screen bg-white px-4 py-12 text-center">Ничего не найдено.</div>,
  component: RequestsPage,
});

type RequestRow = {
  id: string;
  user_id: string;
  title: string;
  category: string;
  city: string | null;
  district: string | null;
  description: string | null;
  photo_url: string | null;
  status: string;
  created_at: string | null;
  is_pinned: boolean;
  solution_response_id: string | null;
};

type ResponseRow = {
  id: string;
  request_id: string;
  sender_user_id: string;
  message: string;
  photo_url: string | null;
  created_at: string;
  parent_id: string | null;
  is_public: boolean;
  hidden_at: string | null;
  deleted_at: string | null;
};

type Person = { user_id: string; full_name: string; photo_url: string | null; city: string | null; district: string | null };

const REQ_COLS = "id, user_id, title, category, city, district, description, photo_url, status, created_at, is_pinned, solution_response_id";
const TITLE_MAX = 90;

async function fetchPublishedRequests() {
  const { data, error } = await supabase
    .from("requests")
    .select(REQ_COLS)
    .eq("status", "published")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as RequestRow[];
}

async function fetchPeople(ids: string[]): Promise<Map<string, Person>> {
  const uniq = Array.from(new Set(ids)).filter(Boolean);
  if (!uniq.length) return new Map();
  const { data } = await supabase.from("profiles").select("user_id, full_name, photo_url, city, district").in("user_id", uniq);
  return new Map(((data ?? []) as Person[]).map((p) => [p.user_id, p]));
}

function useIsAdmin() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("is_admin").eq("user_id", user!.id).maybeSingle();
      return Boolean(data?.is_admin);
    },
  }).data ?? false;
}

function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "сейчас";
  if (mins < 60) return `${mins} мин`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} ч`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} дн`;
  return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return few;
  return many;
}

function Avatar({ p, size = 36 }: { p?: Person; size?: number }) {
  const name = p?.full_name || "?";
  return p?.photo_url ? (
    <img decoding="async" src={p.photo_url} alt={name} className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} loading="lazy" />
  ) : (
    <span className="flex shrink-0 items-center justify-center rounded-full bg-td-soft text-xs font-semibold text-td-muted" style={{ width: size, height: size }}>
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

async function uploadPhoto(userId: string, file: File, folder: string): Promise<string> {
  const upFile = await compressImage(file, { maxSide: PHOTO_MAX_SIDE });
  const ext = fileExt(upFile);
  const path = `${userId}/${folder}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("marketplace").upload(path, upFile, { contentType: upFile.type, cacheControl: LONG_CACHE });
  if (error) throw error;
  return supabase.storage.from("marketplace").getPublicUrl(path).data.publicUrl;
}

/* ================================== Page ================================== */

function RequestsPage() {
  const { request } = Route.useSearch();
  const [asking, setAsking] = useState(false);
  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-clip bg-white">
      {request ? <ThreadScreen key={request} requestId={request} /> : <FeedScreen onAsk={() => setAsking(true)} />}
      <AskDialog open={asking} onOpenChange={setAsking} />
    </div>
  );
}

/* ================================== Feed ================================== */

function FeedScreen({ onAsk }: { onAsk: () => void }) {
  const { user } = useAuth();
  const { data: rows } = useSuspenseQuery({ queryKey: ["requests", "published"], queryFn: fetchPublishedRequests });
  const [q, setQ] = useState("");
  const [myCity, setMyCity] = useState(false);
  const [topic, setTopic] = useState<string | null>(null);
  const [unanswered, setUnanswered] = useState(false);

  const { data: me } = useQuery({
    queryKey: ["profile-city", user?.id],
    enabled: !!user,
    queryFn: async () => (await supabase.from("profiles").select("city").eq("user_id", user!.id).maybeSingle()).data,
  });

  const ids = rows.map((r) => r.id);
  const { data: counts = new Map<string, number>() } = useQuery({
    queryKey: ["requests", "counts", ids.length, ids[0]],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("request_responses").select("request_id")
        .in("request_id", ids).eq("is_public", true).is("hidden_at", null).is("deleted_at", null);
      const m = new Map<string, number>();
      for (const r of data ?? []) m.set(r.request_id, (m.get(r.request_id) ?? 0) + 1);
      return m;
    },
  });
  const { data: authors = new Map<string, Person>() } = useQuery({
    queryKey: ["requests", "authors", ids.length, ids[0]],
    queryFn: () => fetchPeople(rows.map((r) => r.user_id)),
  });

  const term = q.trim();
  const { data: answerHits } = useQuery({
    queryKey: ["requests", "answer-search", term],
    enabled: term.length >= 2,
    queryFn: async () => {
      const { data } = await supabase.from("request_responses").select("request_id")
        .ilike("message", `%${term.replace(/[%_]/g, "")}%`).eq("is_public", true).limit(200);
      return new Set((data ?? []).map((r) => r.request_id));
    },
  });

  const pinned = rows.find((r) => r.is_pinned);
  const list = useMemo(() => {
    const t = term.toLowerCase();
    return rows.filter((r) => {
      if (r.is_pinned && !t && !topic && !myCity && !unanswered) return false;
      if (myCity && me?.city && r.city !== me.city) return false;
      if (topic && topicOf(r.category) !== topic) return false;
      if (unanswered && (counts.get(r.id) ?? 0) > 0) return false;
      if (t.length >= 2) {
        const inText = `${r.title} ${r.description ?? ""}`.toLowerCase().includes(t);
        if (!inText && !answerHits?.has(r.id)) return false;
      }
      return true;
    });
  }, [rows, term, topic, myCity, unanswered, me?.city, counts, answerHits]);

  const chip = (active: boolean) =>
    "shrink-0 rounded-full border px-3 py-1.5 text-[13px] transition " +
    (active ? "border-td-teal bg-td-teal text-white" : "border-td-line bg-white text-td-dark hover:border-td-teal/60");

  return (
    <main className="mx-auto w-full min-w-0 max-w-[640px] overflow-x-clip px-4 pb-28 pt-5">
      <RequestsModeration />
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-3 overflow-hidden">
        <div className="min-w-0">
          <h1 className="font-serif text-3xl text-td-dark md:text-4xl">Спроси своих</h1>
          <p className="mt-2 text-sm text-td-muted md:text-base">Задай вопрос своим: поделятся опытом, подскажут, посоветуют.</p>
          <Button onClick={onAsk} className="mt-3 rounded-full bg-td-teal hover:bg-td-teal/90">
            <Plus className="mr-1 h-4 w-4" />Задать вопрос
          </Button>
        </div>
        <div className="shrink-0 pr-2 sm:hidden">
          <MrDriveAvatar variant="requests" size={100} />
        </div>
        <div className="hidden shrink-0 pr-3 sm:block">
          <MrDriveAvatar variant="requests" size={168} />
        </div>
      </div>

      {pinned && <PinnedCard r={pinned} count={counts.get(pinned.id) ?? 0} author={authors.get(pinned.user_id)} />}

      <div className="relative mt-4 min-w-0">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-td-muted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск по вопросам и ответам" className="h-11 w-full min-w-0 rounded-full pl-9 text-base md:text-sm" />
      </div>
      <div className="mt-3 flex w-full max-w-full gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:none] [touch-action:pan-x_pan-y]">
        {me?.city && <button type="button" className={chip(myCity)} onClick={() => setMyCity((v) => !v)}>Мой город</button>}
        <button type="button" className={chip(unanswered)} onClick={() => setUnanswered((v) => !v)}>Без ответа</button>
        {THREAD_TOPICS.map((t) => (
          <button key={t} type="button" className={chip(topic === t)} onClick={() => setTopic((c) => (c === t ? null : t))}>{t}</button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="mt-10 text-center">
          <MrDriveAvatar variant="requests" size={72} borderWidth={3} className="mx-auto mb-3" />
          <p className="text-sm text-td-muted">Пока таких вопросов нет. Задай свой, и свои подскажут.</p>
          <Button onClick={onAsk} variant="outline" className="mt-3 rounded-full">Задать вопрос</Button>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-td-line border-y border-td-line">
          {list.map((r) => <FeedItem key={r.id} r={r} count={counts.get(r.id) ?? 0} author={authors.get(r.user_id)} />)}
        </ul>
      )}
    </main>
  );
}

function PinnedCard({ r, count, author }: { r: RequestRow; count: number; author?: Person }) {
  return (
    <Link to="/requests" search={{ request: r.id }} className="mt-4 block min-w-0 rounded-2xl border border-td-teal/30 bg-td-teal/5 p-4">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-td-teal"><Pin className="h-3.5 w-3.5" /> Ветка недели</div>
      <p className="mt-1.5 text-[15px] font-semibold leading-snug text-td-dark [overflow-wrap:anywhere]">{r.title}</p>
      <p className="mt-1 text-xs text-td-muted [overflow-wrap:anywhere]">
        {[author?.full_name, `${count} ${plural(count, "ответ", "ответа", "ответов")}`].filter(Boolean).join(" · ")}
      </p>
    </Link>
  );
}

function FeedItem({ r, count, author }: { r: RequestRow; count: number; author?: Person }) {
  const place = formatPlace(r.city, r.district);
  return (
    <li>
      <Link to="/requests" search={{ request: r.id }} className="flex min-w-0 gap-3 py-4">
        <Avatar p={author} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-td-muted">
            <span className="truncate font-medium text-td-dark">{author?.full_name ?? "Резидент"}</span>
            <span>·</span>
            <span className="shrink-0">{timeAgo(r.created_at)}</span>
          </div>
          <p className="mt-0.5 line-clamp-2 text-[15px] font-semibold leading-snug text-td-dark [overflow-wrap:anywhere]">{r.title}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
            <span className="min-w-0 max-w-full rounded-full bg-td-soft px-2 py-0.5 text-td-dark/80 [overflow-wrap:anywhere]">{r.category}</span>
            {place && <span className="min-w-0 max-w-full truncate text-td-muted">{place}</span>}
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-td-muted">
            <span className="inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" />{count} {plural(count, "ответ", "ответа", "ответов")}</span>
            {r.solution_response_id && <span className="inline-flex items-center gap-1 font-medium text-td-teal"><CheckCircle2 className="h-3.5 w-3.5" />Решено</span>}
          </div>
        </div>
      </Link>
    </li>
  );
}

/* =============================== Ask dialog =============================== */

function AskDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { user } = useAuth();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [topic, setTopic] = useState<string>("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [photo, setPhoto] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    supabase.from("profiles").select("city, district").eq("user_id", user.id).maybeSingle().then(({ data }) => {
      setCity((c) => c || data?.city || "");
      setDistrict((d) => d || data?.district || "");
    });
  }, [open, user]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    setUploading(true);
    try { setPhoto(await uploadPhoto(user.id, file, "requests")); } catch { toast.error("Не удалось загрузить фото"); }
    setUploading(false);
  }

  async function submit() {
    if (!user) return;
    if (!title.trim()) return toast.error("Напиши вопрос");
    if (!topic) return toast.error("Выбери тему");
    setSaving(true);
    const { data, error } = await supabase.from("requests").insert({
      user_id: user.id,
      title: title.trim().slice(0, TITLE_MAX),
      description: details.trim() || null,
      category: topic,
      city: city || null,
      district: district.trim() || null,
      photo_url: photo,
      request_type: "Ищу",
      status: "published",
    }).select("id").single();
    setSaving(false);
    if (error || !data) return toast.error("Не получилось опубликовать, попробуй ещё раз");
    toast.success("Вопрос опубликован");
    setTitle(""); setDetails(""); setTopic(""); setPhoto(null);
    onOpenChange(false);
    qc.invalidateQueries({ queryKey: ["requests"] });
    navigate({ to: "/requests", search: { request: data.id } });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader><DialogTitle>Задать вопрос</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="ask-title">Вопрос</Label>
            <Input id="ask-title" value={title} maxLength={TITLE_MAX} onChange={(e) => setTitle(e.target.value)} placeholder="Например: где сделать ВНЖ ребёнку?" className="mt-1.5 h-11 text-base md:text-sm" />
            <p className="mt-1 text-right text-xs text-td-muted">{title.length}/{TITLE_MAX}</p>
          </div>
          <div>
            <Label htmlFor="ask-details">Подробности</Label>
            <Textarea id="ask-details" value={details} onChange={(e) => setDetails(e.target.value)} rows={4} placeholder="Расскажи чуть больше, так ответят точнее" className="mt-1.5 text-base md:text-sm" />
          </div>
          <div>
            <Label>Тема</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {THREAD_TOPICS.map((t) => (
                <button key={t} type="button" onClick={() => setTopic(t)}
                  className={"min-h-9 rounded-full border px-3 text-sm " + (topic === t ? "border-td-teal bg-td-teal text-white" : "border-td-line text-td-dark")}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Город</Label>
            <div className="mt-1.5"><CitySelect value={city} onChange={(c) => { setCity(c); setDistrict(""); }} /></div>
          </div>
          {city && (
            <div>
              <Label>Район</Label>
              <div className="mt-1.5"><DistrictSelect city={city} district={district} onChange={(d) => setDistrict(d)} withNeighborhood={false} /></div>
            </div>
          )}
          <div>
            {photo ? (
              <div className="relative inline-block">
                <img loading="lazy" decoding="async" src={photo} alt="" className="h-24 max-w-full rounded-xl object-cover" />
                <button type="button" onClick={() => setPhoto(null)} className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow" aria-label="Убрать фото"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full border border-td-line px-4 text-sm text-td-dark">
                <input type="file" accept="image/*" className="sr-only" onChange={onFile} disabled={uploading} />
                <ImagePlus className="h-4 w-4" /> {uploading ? "Загружаем…" : "Добавить фото"}
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || uploading} className="h-11 w-full rounded-full bg-td-teal hover:bg-td-teal/90">
            {saving ? "Публикуем…" : "Опубликовать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================ Thread ================================== */

type Thread = {
  request: RequestRow | null;
  responses: ResponseRow[];
  people: Map<string, Person>;
  votes: Map<string, number>;
  myVotes: Set<string>;
  tombstones: Map<string, { state: string; created_at: string }>;
};

function ThreadScreen({ requestId }: { requestId: string }) {
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const [replyTo, setReplyTo] = useState<ResponseRow | null>(null);
  const [showCoffee, setShowCoffee] = useState(false);
  const [reportId, setReportId] = useState<string | null>(null);
  const key = ["requests", "thread", requestId];

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async (): Promise<Thread> => {
      const { data: request } = await supabase.from("requests").select(REQ_COLS).eq("id", requestId).maybeSingle();
      if (!request) return { request: null, responses: [], people: new Map(), votes: new Map(), myVotes: new Set(), tombstones: new Map() };
      const [{ data: resp }, { data: tomb }] = await Promise.all([
        supabase.from("request_responses").select("id, request_id, sender_user_id, message, photo_url, created_at, parent_id, is_public, hidden_at, deleted_at")
          .eq("request_id", requestId).order("created_at", { ascending: true }),
        supabase.rpc("request_thread_tombstones", { p_request: requestId }),
      ]);
      const responses = (resp ?? []) as ResponseRow[];
      const ids = responses.map((r) => r.id);
      const { data: v } = ids.length
        ? await supabase.from("request_response_votes").select("response_id, user_id").in("response_id", ids)
        : { data: [] as { response_id: string; user_id: string }[] };
      const votes = new Map<string, number>();
      const myVotes = new Set<string>();
      for (const x of v ?? []) {
        votes.set(x.response_id, (votes.get(x.response_id) ?? 0) + 1);
        if (x.user_id === user?.id) myVotes.add(x.response_id);
      }
      const people = await fetchPeople([(request as RequestRow).user_id, ...responses.map((r) => r.sender_user_id)]);
      const tombstones = new Map(((tomb ?? []) as { id: string; state: string; created_at: string }[]).map((t) => [t.id, t]));
      return { request: request as RequestRow, responses, people, votes, myVotes, tombstones };
    },
  });

  const req = data?.request;
  const unavailable = !isLoading && (!req || (req.status !== "published" && req.user_id !== user?.id && !isAdmin));
  useEffect(() => {
    if (unavailable) {
      toast("Этот вопрос больше недоступен");
      navigate({ to: "/requests", search: {}, replace: true });
    }
  }, [unavailable, navigate]);

  // Прокрутка к ответу по якорю из уведомления
  const scrolled = useRef(false);
  useEffect(() => {
    if (!data || scrolled.current || typeof window === "undefined") return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    scrolled.current = true;
    requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "center" }));
  }, [data]);

  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const updateResponse = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: { deleted_at?: string; hidden_at?: string | null } }) => {
      const { error } = await supabase.from("request_responses").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: refresh,
    onError: () => toast.error("Не получилось, попробуй ещё раз"),
  });
  const updateRequest = useMutation({
    mutationFn: async (patch: { solution_response_id?: string | null; is_pinned?: boolean; status?: string }) => {
      const { error } = await supabase.from("requests").update(patch).eq("id", requestId);
      if (error) throw error;
    },
    onSuccess: () => { refresh(); qc.invalidateQueries({ queryKey: ["requests", "published"] }); qc.invalidateQueries({ queryKey: ["home"] }); },
    onError: () => toast.error("Не получилось, попробуй ещё раз"),
  });
  const vote = useMutation({
    mutationFn: async ({ id, on }: { id: string; on: boolean }) => {
      if (!user) return;
      const { error } = on
        ? await supabase.from("request_response_votes").insert({ response_id: id, user_id: user.id })
        : await supabase.from("request_response_votes").delete().eq("response_id", id).eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  if (isLoading || !data || !req || unavailable) {
    return <main className="mx-auto max-w-[640px] px-4 py-10 text-center text-sm text-td-muted">Загружаем ветку…</main>;
  }

  const isOwner = req.user_id === user?.id;
  const author = data.people.get(req.user_id);
  const publicRows = data.responses.filter((r) => r.is_public);
  const privateRows = data.responses.filter((r) => !r.is_public);
  const visible = (r: ResponseRow) => !r.hidden_at && !r.deleted_at;
  const children = (pid: string) => publicRows.filter((r) => r.parent_id === pid && (visible(r) || isAdmin || r.sender_user_id === user?.id));

  type Top = ResponseRow & { votes: number; tomb?: string };
  const tops: Top[] = publicRows
    .filter((r) => !r.parent_id)
    .filter((r) => visible(r) || isAdmin || r.sender_user_id === user?.id || children(r.id).length > 0)
    .map((r) => ({ ...r, votes: data.votes.get(r.id) ?? 0, tomb: !visible(r) && !isAdmin ? (r.hidden_at ? "hidden" : "deleted") : undefined }));
  // Скрытые/удалённые родители, которых RLS не отдаёт, но под ними есть ответы
  for (const [id, t] of data.tombstones) {
    if (!tops.some((x) => x.id === id)) {
      tops.push({ id, request_id: req.id, sender_user_id: "", message: "", photo_url: null, created_at: t.created_at, parent_id: null, is_public: true, hidden_at: t.state === "hidden" ? t.created_at : null, deleted_at: t.state === "deleted" ? t.created_at : null, votes: 0, tomb: t.state });
    }
  }
  const sorted = sortTopLevel(tops, req.solution_response_id);
  const publicCount = publicRows.filter(visible).length;
  const expertCat = expertCategoryFor(req.category);
  const place = formatPlace(req.city, req.district);

  const renderItem = (r: ResponseRow, nested: boolean, tomb?: string) => {
    if (tomb) {
      return <p className="py-2 text-sm italic text-td-muted">{tomb === "hidden" ? "Ответ скрыт модератором" : "Ответ удалён"}</p>;
    }
    const p = data.people.get(r.sender_user_id);
    const mine = r.sender_user_id === user?.id;
    const votes = data.votes.get(r.id) ?? 0;
    const voted = data.myVotes.has(r.id);
    const isSolution = req.solution_response_id === r.id;
    return (
      <div id={`r-${r.id}`} className="flex min-w-0 scroll-mt-24 gap-3">
        <Avatar p={p} size={nested ? 28 : 36} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-td-muted">
            <Link to="/people/$userId" params={{ userId: r.sender_user_id }} className="truncate font-semibold text-td-dark">{p?.full_name ?? "Резидент"}</Link>
            <span>·</span><span className="shrink-0">{timeAgo(r.created_at)}</span>
            {isSolution && <span className="ml-1 inline-flex shrink-0 items-center gap-0.5 rounded-full bg-td-teal/10 px-2 py-0.5 font-semibold text-td-teal"><CheckCircle2 className="h-3 w-3" />Решение</span>}
          </div>
          {(r.hidden_at || r.deleted_at) && (
            <p className="mt-0.5 text-xs font-medium text-td-orange">{r.hidden_at ? "Скрыт модератором" : "Удалён"}{mine && r.hidden_at ? ", видишь только ты" : ""}</p>
          )}
          <p className="mt-0.5 whitespace-pre-line break-words [overflow-wrap:anywhere] text-[15px] leading-relaxed text-td-dark"><MessageText text={r.message} /></p>
          {r.photo_url && <img decoding="async" src={r.photo_url} alt="" className="mt-2 max-h-72 max-w-full rounded-xl object-cover" loading="lazy" />}
          {r.is_public && visible(r) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[13px] text-td-muted">
              <button type="button" className="min-h-9 rounded-full px-2 hover:bg-td-soft" onClick={() => setReplyTo(r)}>Ответить</button>
              <button type="button" disabled={mine || vote.isPending}
                className={"inline-flex min-h-9 items-center gap-1 rounded-full px-2 hover:bg-td-soft disabled:opacity-60 " + (voted ? "font-semibold text-td-teal" : "")}
                onClick={() => vote.mutate({ id: r.id, on: !voted })}>
                <ThumbsUp className="h-3.5 w-3.5" /> Полезно · {votes}
              </button>
              {isOwner && !nested && !isSolution && (
                <button type="button" className="min-h-9 rounded-full px-2 text-td-teal hover:bg-td-soft" onClick={() => updateRequest.mutate({ solution_response_id: r.id })}>Отметить как решение</button>
              )}
              {isOwner && isSolution && (
                <button type="button" className="min-h-9 rounded-full px-2 hover:bg-td-soft" onClick={() => updateRequest.mutate({ solution_response_id: null })}>Снять решение</button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="ml-auto flex h-9 w-9 items-center justify-center rounded-full hover:bg-td-soft" aria-label="Ещё"><MoreHorizontal className="h-4 w-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {mine ? (
                    <DropdownMenuItem onClick={() => { if (confirm("Удалить ответ?")) updateResponse.mutate({ id: r.id, patch: { deleted_at: new Date().toISOString() } }); }}>Удалить</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setReportId(r.id)}>Пожаловаться</DropdownMenuItem>
                  )}
                  {isAdmin && <DropdownMenuItem onClick={() => updateResponse.mutate({ id: r.id, patch: { hidden_at: new Date().toISOString() } })}>Скрыть ответ</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          {isAdmin && r.hidden_at && (
            <button type="button" className="mt-1 min-h-9 rounded-full px-2 text-[13px] text-td-teal hover:bg-td-soft" onClick={() => updateResponse.mutate({ id: r.id, patch: { hidden_at: null } })}>Вернуть ответ</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <main className="mx-auto w-full min-w-0 max-w-[640px] overflow-x-clip px-4 pb-48 pt-4">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <Link to="/requests" search={{}} className="inline-flex min-h-11 items-center gap-1 text-sm text-td-muted hover:text-td-dark">
          <ArrowLeft className="h-4 w-4" /> Спроси своих
        </Link>
        {req.status === "published" && <ShareRequestButton requestId={req.id} title={req.title} />}
      </div>

      {/* Вопрос */}
      <article className="mt-2 border-b border-td-line pb-4">
        {req.is_pinned && <div className="mb-2 flex items-center gap-1 text-xs font-semibold text-td-teal"><Pin className="h-3.5 w-3.5" />Ветка недели</div>}
        {req.status === "hidden" && <p className="mb-2 text-xs font-medium text-td-orange">Вопрос скрыт модератором</p>}
        <div className="flex min-w-0 items-center gap-3">
          <Avatar p={author} size={40} />
          <div className="min-w-0 flex-1">
            <Link to="/people/$userId" params={{ userId: req.user_id }} className="block truncate text-sm font-semibold text-td-dark">{author?.full_name ?? "Резидент"}</Link>
            <p className="truncate text-xs text-td-muted">{[place, timeAgo(req.created_at)].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <h1 className="mt-3 break-words [overflow-wrap:anywhere] text-lg font-bold leading-snug text-td-dark">{req.title}</h1>
        {req.description && req.description !== req.title && (
          <p className="mt-2 whitespace-pre-line break-words [overflow-wrap:anywhere] text-[15px] leading-relaxed text-td-dark/90">{req.description}</p>
        )}
        {req.photo_url && <img loading="lazy" decoding="async" src={req.photo_url} alt="" className="mt-3 max-h-80 w-full max-w-full rounded-2xl object-cover" />}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="min-w-0 max-w-full rounded-full bg-td-soft px-2.5 py-1 text-td-dark/80 [overflow-wrap:anywhere]">{req.category}</span>
          <span className="text-td-muted">{publicCount} {plural(publicCount, "ответ", "ответа", "ответов")}</span>
          {!isOwner && (
            <button type="button" onClick={() => setShowCoffee(true)} className="ml-auto inline-flex min-h-9 shrink-0 items-center gap-1 rounded-full border border-td-line px-3 text-[13px] text-td-dark">
              <Coffee className="h-3.5 w-3.5" /> Позвать на кофе
            </button>
          )}
        </div>
        {(isAdmin || isOwner) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {isAdmin && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => updateRequest.mutate({ is_pinned: !req.is_pinned })}>
                <Pin className="mr-1 h-3.5 w-3.5" />{req.is_pinned ? "Открепить" : "Закрепить"}
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => updateRequest.mutate({ status: req.status === "hidden" ? "published" : "hidden" })}>
                {req.status === "hidden" ? <><Eye className="mr-1 h-3.5 w-3.5" />Вернуть вопрос</> : <><EyeOff className="mr-1 h-3.5 w-3.5" />Скрыть вопрос</>}
              </Button>
            )}
            {isOwner && req.status === "published" && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => {
                if (confirm("Убрать вопрос из ленты?")) updateRequest.mutate({ status: "withdrawn" }, { onSuccess: () => navigate({ to: "/requests", search: {} }) });
              }}>Убрать вопрос</Button>
            )}
          </div>
        )}
      </article>

      {expertCat && (
        <Link to="/people" search={{ experts: true, category: expertCat }} className="mt-4 flex items-center gap-3 rounded-2xl border border-td-teal/30 bg-td-teal/5 p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-td-teal text-white"><Stethoscope className="h-4 w-4" /></span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-td-dark">Эксперты по теме</span>
            <span className="block text-xs text-td-muted">{expertCat}: резиденты, которые помогают профессионально</span>
          </span>
          <span className="text-sm text-td-teal">→</span>
        </Link>
      )}

      {/* Ветка */}
      {sorted.length === 0 ? (
        <div className="mt-8 text-center">
          <MrDriveAvatar variant="requests" size={72} borderWidth={3} className="mx-auto mb-3" />
          <p className="text-sm text-td-muted">Пока нет ответов. Будь первым, кто подскажет.</p>
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-td-line">
          {sorted.map((r) => {
            const kids = children(r.id);
            return (
              <li key={r.id} className={"py-4 " + (req.solution_response_id === r.id ? "-mx-2 rounded-xl bg-td-teal/5 px-2" : "")}>
                {renderItem(r, false, r.tomb)}
                {kids.length > 0 && (
                  <ul className="ml-[18px] mt-3 space-y-4 border-l-2 border-td-line pl-4">
                    {kids.map((c) => <li key={c.id}>{renderItem(c, true, !visible(c) && !isAdmin && c.sender_user_id !== user?.id ? (c.hidden_at ? "hidden" : "deleted") : undefined)}</li>)}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Старые личные ответы */}
      {privateRows.length > 0 && (isOwner || isAdmin) && (
        <section className="mt-6 rounded-2xl border border-td-line bg-td-soft/40 p-4">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-td-dark"><Lock className="h-3.5 w-3.5" />Личные ответы (видишь только ты)</h2>
          <ul className="mt-3 space-y-4">
            {privateRows.map((r) => <li key={r.id}>{renderItem(r, false)}</li>)}
          </ul>
        </section>
      )}
      {privateRows.length > 0 && !isOwner && !isAdmin && (
        <section className="mt-6 rounded-2xl border border-td-line bg-td-soft/40 p-4">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-td-dark"><Lock className="h-3.5 w-3.5" />Твой личный ответ автору</h2>
          <ul className="mt-3 space-y-4">{privateRows.map((r) => <li key={r.id}>{renderItem(r, false)}</li>)}</ul>
        </section>
      )}

      {req.status === "published" && <Composer requestId={req.id} replyTo={replyTo} replyName={replyTo ? data.people.get(replyTo.sender_user_id)?.full_name : undefined} onClearReply={() => setReplyTo(null)} onSent={refresh} />}

      {showCoffee && (
        <InviteToCoffeeDialog open={showCoffee} onOpenChange={setShowCoffee} recipientUserId={req.user_id}
          initialMessage={`Привет! Увидел(а) твой вопрос „${req.title}“, давай встретимся на кофе?`} />
      )}
      <ReportDialog responseId={reportId} onClose={() => setReportId(null)} />
    </main>
  );
}

function MessageText({ text }: { text: string }) {
  return (
    <>
      {parseMentions(text).map((part, i) =>
        part.type === "text" ? <span key={i} className="[overflow-wrap:anywhere]">{part.text}</span> : (
          <Link key={i} to="/people/$userId" params={{ userId: part.userId }} className="font-semibold text-td-teal hover:underline [overflow-wrap:anywhere]">@{part.name}</Link>
        ),
      )}
    </>
  );
}

type Picked = { name: string; userId: string };
const draftKey = (id: string) => `td:reply-draft:${id}`;

function Composer({ requestId, replyTo, replyName, onClearReply, onSent }: {
  requestId: string; replyTo: ResponseRow | null; replyName?: string; onClearReply: () => void; onSent: () => void;
}) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<Picked[]>([]);
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mq, setMq] = useState<{ query: string; start: number } | null>(null);
  const [kbOffset, setKbOffset] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (replyTo) ref.current?.focus(); }, [replyTo]);

  // Черновик для каждого вопроса
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey(requestId));
      if (raw) { const d = JSON.parse(raw); setText(d.text ?? ""); setPicked(d.picked ?? []); }
    } catch { /* ignore */ }
  }, [requestId]);
  useEffect(() => {
    try {
      if (text.trim()) localStorage.setItem(draftKey(requestId), JSON.stringify({ text, picked }));
      else localStorage.removeItem(draftKey(requestId));
    } catch { /* ignore */ }
  }, [text, picked, requestId]);

  // Рост поля: от 1 до ~8 строк, не выше 40% экрана
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const max = Math.min(8 * 24 + 20, window.innerHeight * 0.4);
    el.style.height = Math.min(el.scrollHeight, max) + "px";
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [text]);

  // Подъём над клавиатурой на iPhone
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const upd = () => setKbOffset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    upd();
    vv.addEventListener("resize", upd); vv.addEventListener("scroll", upd);
    return () => { vv.removeEventListener("resize", upd); vv.removeEventListener("scroll", upd); };
  }, []);

  const activePicked = picked.filter((p) => text.includes("@" + p.name));
  const { data: suggestions } = useQuery({
    queryKey: ["mention-search", mq?.query],
    enabled: !!mq && mq.query.length >= 1 && activePicked.length < MAX_MENTIONS,
    queryFn: async () => {
      const q = mq!.query.replace(/[%_,()]/g, "");
      const { data } = await supabase.from("profiles").select("user_id, full_name, city, district, photo_url")
        .ilike("full_name", `%${q}%`).is("deletion_requested_at", null).neq("user_id", user?.id ?? "").limit(6);
      return (data ?? []) as Person[];
    },
  });

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);
    setMq(mentionQueryAt(e.target.value, e.target.selectionStart ?? e.target.value.length));
  }

  function pick(p: Person) {
    if (!mq) return;
    const name = (p.full_name || "Резидент").replace(/[[\]]/g, "").trim();
    const end = mq.start + 1 + mq.query.length;
    const next = text.slice(0, mq.start) + "@" + name + " " + text.slice(end);
    setText(next);
    setPicked((prev) => [...prev.filter((x) => x.userId !== p.user_id), { name, userId: p.user_id }]);
    setMq(null);
    requestAnimationFrame(() => {
      const pos = mq.start + name.length + 2;
      ref.current?.focus(); ref.current?.setSelectionRange(pos, pos);
    });
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    setBusy(true);
    try { setPhoto(await uploadPhoto(user.id, file, "request-responses")); } catch { toast.error("Не удалось загрузить фото"); }
    setBusy(false);
  }

  async function send() {
    if (!user || !text.trim()) return;
    setBusy(true);
    const { error } = await supabase.from("request_responses").insert({
      request_id: requestId,
      sender_user_id: user.id,
      message: encodeMentions(text.trim(), activePicked, user.id),
      photo_url: photo,
      parent_id: replyTo ? (replyTo.parent_id ?? replyTo.id) : null,
    });
    setBusy(false);
    if (error) return toast.error("Не получилось отправить, попробуй ещё раз");
    try { localStorage.removeItem(draftKey(requestId)); } catch { /* ignore */ }
    setText(""); setPicked([]); setPhoto(null); setMq(null); onClearReply(); onSent();
  }

  const showList = !!mq && mq.query.length >= 1 && (suggestions?.length ?? 0) > 0;

  return (
    <div
      className="td-composer-pos fixed left-0 right-0 z-30 w-full max-w-full overflow-hidden border-t border-td-line bg-white/95 backdrop-blur md:pb-[env(safe-area-inset-bottom)]"
      style={{ bottom: kbOffset > 80 ? kbOffset : undefined }}
    >
      <style>{`.td-composer-pos{bottom:calc(env(safe-area-inset-bottom) + 64px)}@media(min-width:768px){.td-composer-pos{bottom:0}}`}</style>
      <div className="mx-auto w-full min-w-0 max-w-[640px] px-3 py-2">
        {showList && (
          <ul className="mb-1.5 max-h-72 w-full max-w-full overflow-y-auto overflow-x-hidden rounded-2xl border border-td-line bg-white py-1 shadow-lg">
            {suggestions!.map((p) => (
              <li key={p.user_id}>
                <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)}
                  className="flex min-h-11 w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-td-soft">
                  <Avatar p={p} size={28} />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-td-dark">{p.full_name}</span>
                  {p.city && <span className="max-w-[40%] shrink-0 truncate text-xs text-td-muted">{p.city}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {mq && activePicked.length >= MAX_MENTIONS && (
          <p className="mb-1.5 text-xs text-td-muted">В одном ответе можно отметить до {MAX_MENTIONS} человек</p>
        )}
        {replyTo && (
          <div className="mb-1.5 flex min-w-0 items-center justify-between gap-2 rounded-full bg-td-soft px-3 py-1 text-xs text-td-dark">
            <span className="truncate">Ответ для {replyName ?? "резидента"}</span>
            <button type="button" onClick={onClearReply} aria-label="Отменить ответ" className="shrink-0"><X className="h-3.5 w-3.5" /></button>
          </div>
        )}
        {photo && (
          <div className="relative mb-1.5 inline-block">
            <img loading="lazy" decoding="async" src={photo} alt="" className="h-14 max-w-full rounded-lg object-cover" />
            <button type="button" onClick={() => setPhoto(null)} className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow" aria-label="Убрать фото"><X className="h-3 w-3" /></button>
          </div>
        )}
        <div className="flex w-full min-w-0 items-end gap-2">
          <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-td-muted hover:bg-td-soft" aria-label="Добавить фото">
            <input type="file" accept="image/*" className="sr-only" onChange={onFile} disabled={busy} />
            <ImagePlus className="h-5 w-5" />
          </label>
          <textarea ref={ref} value={text} onChange={onChange} rows={1} placeholder="Напиши ответ… Отметь резидента через @"
            onKeyUp={(e) => { const t = e.currentTarget; setMq(mentionQueryAt(t.value, t.selectionStart ?? t.value.length)); }}
            onBlur={() => setTimeout(() => setMq(null), 150)}
            className="min-h-11 min-w-0 flex-1 resize-none rounded-2xl border border-td-line bg-white px-3 py-2.5 text-base leading-6 text-td-dark outline-none md:text-[15px] focus:border-td-teal" />
          <button type="button" onClick={send} disabled={busy || !text.trim()} aria-label="Отправить"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-td-teal text-white disabled:opacity-50">
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-1 pl-12 text-[11px] text-td-muted">Ответ увидят все резиденты</p>
      </div>
    </div>
  );
}

function ReportDialog({ responseId, onClose }: { responseId: string | null; onClose: () => void }) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  async function submit() {
    if (!user || !responseId) return;
    const { error } = await supabase.from("request_response_reports").insert({ response_id: responseId, user_id: user.id, reason: reason.trim() || null });
    if (error && !error.message.includes("duplicate")) return toast.error("Не получилось отправить жалобу");
    toast.success("Спасибо, мы посмотрим");
    setReason(""); onClose();
  }
  return (
    <Dialog open={!!responseId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader><DialogTitle>Пожаловаться на ответ</DialogTitle></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Что не так? (необязательно)" className="text-base md:text-sm" />
        <DialogFooter>
          <Button onClick={submit} className="h-11 w-full rounded-full bg-td-teal hover:bg-td-teal/90">Отправить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
